import type {
  ImportResult,
  LinkCreate,
  LinkIcon,
  LinkOpenIn,
  LinkSource,
  LinkUpdate,
  PublicUser,
  SectionCreate,
  SectionLayout,
  SectionUpdate,
  UserRole,
  UserSettings,
} from '@flame/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: unknown
  ) {
    super(code);
  }
}

// Server error codes → something a person can act on.
const MESSAGES: Record<string, string> = {
  duplicate_url: 'You already have a link to that URL.',
  section_name_taken: 'You already have a section with that name.',
  section_not_empty: 'That section still has links in it.',
  last_passkey: "You can't remove your only passkey. Add another one first.",
  username_taken: 'That username is taken.',
  invite_invalid: 'This invite link has expired or was already used.',
  setup_unavailable: 'Setup is closed or the setup link is wrong.',
  passkey_verification_failed: "Your device didn't confirm the passkey. Try again.",
  invalid_passkey: "That passkey isn't registered here.",
  ceremony_expired: 'That took too long. Please try again.',
  rate_limited: 'Too many attempts. Wait a few minutes and try again.',
  unsupported_url_scheme: "That kind of link isn't allowed.",
  invalid_url: "That doesn't look like a URL.",
  cannot_delete_self: "You can't delete your own account.",
  last_admin: "You can't delete the last admin.",
  unauthenticated: 'Please sign in again.',
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return MESSAGES[err.code] ?? err.code.replace(/_/g, ' ');
  if (err instanceof DOMException && err.name === 'NotAllowedError') {
    return 'The passkey prompt was cancelled.';
  }
  return err instanceof Error ? err.message : String(err);
}

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = res.headers.get('content-type')?.includes('json') ? await res.json() : undefined;
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/api/auth/')) {
      window.dispatchEvent(new Event('flame:unauthenticated'));
    }
    throw new ApiError(res.status, (data as { error?: string })?.error ?? `http_${res.status}`, data);
  }
  return data as T;
}

// ---------------------------------------------------------------- types

export interface Link {
  id: string;
  sectionId: string;
  url: string;
  title: string;
  description: string;
  icon: LinkIcon | null;
  tags: string[];
  pinned: boolean;
  openIn: LinkOpenIn;
  position: number;
  source: LinkSource;
  createdByClient: string | null;
  createdAt: string;
}

export interface Section {
  id: string;
  name: string;
  position: number;
  layout: SectionLayout;
  collapsed: boolean;
  pinned: boolean;
  links: Link[];
}

export interface Page {
  sections: Section[];
  settings: UserSettings;
}

export interface PasskeyInfo {
  id: string;
  name: string;
  deviceType: string;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface SessionInfo {
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

export interface AdminUser extends PublicUser {
  createdAt: string;
  passkeys: number;
}

export interface AuditEntry {
  id: number;
  actor: string;
  action: string;
  entityType: string;
  entityId: string | null;
  payload: Record<string, any> | null;
  createdAt: string;
}

// ---------------------------------------------------------------- endpoints

export const Api = {
  state: () => api<{ needsSetup: boolean; user: PublicUser | null }>('GET', '/api/auth/state'),
  logout: () => api('POST', '/api/auth/logout'),

  page: () => api<Page>('GET', '/api/page'),
  createSection: (body: SectionCreate) =>
    api<{ section: Section }>('POST', '/api/sections', body),
  updateSection: (id: string, body: SectionUpdate) =>
    api<{ section: Section }>('PATCH', `/api/sections/${id}`, body),
  deleteSection: (id: string, force = false) =>
    api('DELETE', `/api/sections/${id}${force ? '?force=true' : ''}`),
  orderSections: (ids: string[]) => api('PUT', '/api/sections/order', { ids }),
  orderLinks: (sectionId: string, ids: string[]) =>
    api('PUT', `/api/sections/${sectionId}/order`, { ids }),

  createLink: (body: LinkCreate) => api<{ link: Link }>('POST', '/api/links', body),
  updateLink: (id: string, body: LinkUpdate) =>
    api<{ link: Link }>('PATCH', `/api/links/${id}`, body),
  deleteLink: (id: string) => api('DELETE', `/api/links/${id}`),

  updateSettings: (patch: Partial<{ [K in keyof UserSettings]: Partial<UserSettings[K]> }>) =>
    api<{ settings: UserSettings }>('PATCH', '/api/settings', patch),

  importData: (body: { format: 'yaml' | 'json' | 'netscape'; content: string; mode: 'merge' | 'replace' }) =>
    api<ImportResult>('POST', '/api/data/import', body),
  audit: (before?: number) =>
    api<{ entries: AuditEntry[] }>('GET', `/api/audit?limit=50${before ? `&before=${before}` : ''}`),

  passkeys: () => api<{ passkeys: PasskeyInfo[] }>('GET', '/api/me/passkeys'),
  renamePasskey: (id: string, name: string) =>
    api('PATCH', `/api/me/passkeys/${encodeURIComponent(id)}`, { name }),
  deletePasskey: (id: string) => api('DELETE', `/api/me/passkeys/${encodeURIComponent(id)}`),
  sessions: () => api<{ sessions: SessionInfo[] }>('GET', '/api/me/sessions'),
  revokeSession: (id: string) => api('DELETE', `/api/me/sessions/${id}`),
  revokeOtherSessions: () => api('POST', '/api/me/sessions/revoke-others'),

  users: () => api<{ users: AdminUser[] }>('GET', '/api/admin/users'),
  createInvite: (role: UserRole) =>
    api<{ url: string; expiresAt: string }>('POST', '/api/admin/invites', { role }),
  resetPasskeys: (id: string) =>
    api<{ url: string; expiresAt: string }>('POST', `/api/admin/users/${id}/reset-passkeys`, {}),
  deleteUser: (id: string) => api('DELETE', `/api/admin/users/${id}`),
};
